/** @format */

import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';

import { CookieService } from './cookie.service';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HomrAudiences } from '@shared/data-access/interfaces';
import { Logger } from './logger.service';
import { LoggerLevel } from './enums/logger-level.enum';
import { WINDOW } from './window/window.service';

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly zone = inject(NgZone);
  protected readonly document = inject<HTMLDocument>(DOCUMENT);
  protected readonly window = inject(WINDOW);

  protected readonly cookieService = inject(CookieService);
  protected readonly logger = inject(Logger);

  /**
   * @description
   * Returns a supplied Date formatted as MM-DD-YYYY
   *
   * @returns The formatted date to analytics format: MM-DD-YYYY
   */
  getFormattedDate(currentDate: Date): string {
    // Get the full year
    const year = currentDate.getFullYear();

    // Get the month
    let month = (1 + currentDate.getMonth()).toString();

    // Check to see if leading 0 is needed
    month = month.length > 1 ? month : `0${month}`;

    // Get the date
    let day = currentDate.getDate().toString();

    // Check to see if leading 0 is needed
    day = day.length > 1 ? day : `0${day}`;

    // Return the formatted string
    return `${month}-${day}-${year}`;
  }

  /**
   * @description
   * Returns a supplied Date formatted as HH:mm
   *
   * @returns The formatted time to analytics format: HH:mm
   */
  getFormattedTime(currentDate: Date): string {
    // Get the hours
    let hours = `${currentDate.getUTCHours()}`;

    // Check to see if leading 0 is needed
    hours = hours.length > 1 ? hours : `0${hours}`;

    // Get the minutes
    let minutes = `${currentDate.getUTCMinutes()}`;

    // Check to see if leading 0 is needed
    minutes = minutes.length > 1 ? minutes : `0${minutes}`;

    // Return the formatted string
    return `${hours}:${minutes}`;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#resetAnalytics
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @description
   * Resets/re-initializes the siteData property
   */
  resetAnalytics(): void {
    this.window.siteData = {};
    this.siteData.ErrorID = [];
    this.siteData.ErrorMessage = [];
    this.siteData.EventData = [];
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#resetFields
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @description
   * Deletes fields of of the siteData property
   */
  resetFields(...fields: string[]): void {
    for (const field of fields) {
      delete this.siteData[field];
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setAppID
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @description
   */
  setAppID(appID: string): void {
    this.siteData.appID = appID;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setChain
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param chain - The name of the store chain
   *
   * @description
   * Sets the store chain on the Object.  This is the branch of DICK'S Sporting goods that the customer is currently
   * shopping at (i.e. Golf Galaxy, Field & Stream, DICK'S Sporting Goods, etc.)
   */
  setChain(chain: string): void {
    this.siteData.Chain = chain;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setDYMTerm
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param didYouMeanTerm - The Did You Mean Term
   *
   * @description
   * Sets the Did You Mean Term on the Object.
   */
  setDYMTerm(didYouMeanTerm: string): void {
    this.siteData.DYMTerm = didYouMeanTerm;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setDYMType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param didYouMeanType - The Did You Mean Type, e.g. DYMAuto
   *
   * @description
   * Sets the Did You Mean Type on the Object.
   */
  setDYMType(didYouMeanType: string): void {
    this.siteData.DYMType = didYouMeanType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setEAthlete
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param eAthlete - The hashed email of a user
   *
   * @description
   * Sets the hashed email (EAthlete) on the Object.
   */
  setEAthlete(eAthlete: string): void {
    this.siteData.EAthlete = eAthlete;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setEnteredSearchTerm
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param enteredSearchTerm - The Entered Search Term
   *
   * @description
   * Sets the Entered Search Term on the Object.
   */
  setEnteredSearchTerm(enteredSearchTerm: string): void {
    this.siteData.EnteredSearchTerm = enteredSearchTerm;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setHomrAudiences
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param homrData - The an array of audiences from a page call
   *
   * @description
   * Adds audiences set in homR to site data
   */
  setHomrAudiences(homrData: HomrAudiences[]): void {
    if (!this.siteData) {
      this.resetAnalytics();
    }

    if (!this.siteData.HomrAudiences) {
      // If blank initialize the object
      this.siteData.HomrAudiences = [];
    }

    if (homrData.length > 0) {
      homrData.forEach((item) => {
        this.siteData.HomrAudiences.push(item);
      });
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setErrorId
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param errorId - The error ID(s) to set
   *
   * @description
   * Sets an Array of error IDs which are unique identifies for individual errors
   */
  setErrorId(errorId?: string | string[] | number): void {
    // Error IDs
    let errorIds: string[] = [];

    // Check argument type
    if (typeof errorId === 'string' && errorId.trim() !== '') {
      errorIds.push(errorId);
    } else if (Array.isArray(errorId) && errorId.length > 0) {
      errorIds = errorId;
    } else if (typeof errorId === 'number') {
      // Could be number
      errorIds.push(`${errorId}`);
    }

    if (!Array.isArray(this.siteData.ErrorID)) {
      this.siteData.ErrorID = [];
    }

    this.siteData.ErrorID.push(...errorIds);
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setErrorMessage
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param errorMessage - The error message(s) to set
   *
   * @description
   * Sets an Array of error IDs which are unique identifies for individual errors
   */
  setErrorMessage(errorMessage?: string | string[]): void {
    // Error messages
    let errorMessages: string[] = [];

    // Check argument type
    if (typeof errorMessage === 'string' && errorMessage.trim() !== '') {
      errorMessages.push(errorMessage);
    } else if (Array.isArray(errorMessage) && errorMessage.length > 0) {
      errorMessages = errorMessage;
    }

    if (!Array.isArray(this.siteData.ErrorMessage)) {
      this.siteData.ErrorMessage = [];
    }

    this.siteData.ErrorMessage.push(...errorMessages);
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setEventData
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param eventData - The Event data to set
   *
   * @description
   * Sets the update Event type
   */
  setEventData(eventData: string | string[] | Record<string, unknown>): void {
    // Event data
    let events = [];

    // Check argument type
    if (typeof eventData === 'string' && eventData.trim() !== '') {
      events.push(eventData);
    } else if (Array.isArray(eventData) && eventData.length > 0) {
      events = eventData;
    } else if (typeof eventData === 'object') {
      events.push(eventData);
    }

    if (!Array.isArray(this.siteData.EventData)) {
      this.siteData.EventData = [];
    }

    this.siteData.EventData.push(...events);
  }

  // Getter to ensure siteData is initialized
  protected get siteData(): Window['siteData'] {
    if (!this.window.siteData) {
      this.window.siteData = {};
    }
    return this.window.siteData;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setEventType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param eventType - The current update Event type
   *
   * @description
   * Sets the update Event type
   */
  setEventType(eventType: string): void {
    this.siteData.EventType = eventType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoggedIn
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param loggedIn - The Logged In flag as a String
   *
   * @description
   * Sets the Signed In status on the Object. This denotes whether the user has signed in or not.
   */
  setLoggedIn(loggedIn: string): void {
    this.siteData.LoggedIn = loggedIn;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoggedInOccurred
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param loggedInOccurred - The Logged In Occured flag as a String
   *
   * @description
   * Sets the Signed In Occurred flag on the Object. This denotes whether the user has performed the sign in action.
   */
  setLoggedInOccurred(loggedInOccurred: string): void {
    this.siteData.LoggedInOccured = loggedInOccurred;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoyalty
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param isLoyaltyMember - The loyalty (string) boolean
   *
   * @description
   * Sets the Loyalty on the Object. This denotes whether the user is a loyalty member or not.
   */
  setLoyalty(isLoyaltyMember: string): void {
    this.siteData.Loyalty = isLoyaltyMember;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoyaltyID
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param loyaltyID - The loyalty ID of the current user
   *
   * @description
   * Sets the loyalty ID on the Object.  The unique identifier for each member in the loyalty program
   */
  setLoyaltyID(loyaltyID: string): void {
    this.siteData.LoyaltyID = loyaltyID;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoyaltyPointsBalance
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param loyaltyPointsBalance - The number of loyalty points of the current user
   *
   * @description
   * Sets the loyalty points balance on the Object
   */
  setLoyaltyPointsBalance(loyaltyPointsBalance: string): void {
    this.siteData.LoyaltyPointsBalance = loyaltyPointsBalance;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setRewardValue
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param rewardValue
   *
   * @description
   * Sets the reward value on the siteData object
   */
  setRewardValue(rewardValue: number): void {
    this.siteData.rewardValue = rewardValue;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setModalName
   * @methodOf dcsg.app.services.service:setModalName
   *
   * @param modalName - The name of the modal opened
   *
   * @description
   * Sets the Modal name on the Object
   */
  setModalName(modalName: string): void {
    this.siteData.ModalName = modalName;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPage
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param pageTitle - The title of the current state
   *
   * @description
   * Sets the page title of the current state. Duplicate of siteData.PageName
   */
  setPage(pageTitle: string): void {
    this.siteData.Page = pageTitle;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPageName
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param pageTitle - The title of the current state
   *
   * @description
   * Sets the page title of the current state.  Duplicate of siteData.Page
   */
  setPageName(pageTitle: string): void {
    this.siteData.PageName = pageTitle;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPageTemplate
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param template - The template name of the current state
   *
   * @description
   * Sets the page template of the current state
   */
  setPageTemplate(template: string): void {
    this.siteData.PageTemplate = template;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPageType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param type - The type of the current state
   *
   * @description
   * Sets the page type of the current state
   */
  setPageType(type: string): void {
    this.siteData.PageType = type;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPageUrl
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param pageUrl - The page URL
   *
   * @description
   * Sets the Page URL on the Object.  This is the URL of the current page
   */
  setPageUrl(pageUrl: string | undefined): void {
    this.siteData.PageURL = pageUrl ? decodeURI(pageUrl) : '';
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setPromotionMessages
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param promotions - The current promotions
   *
   * @description
   * Sets the current promotion messages displayed on the page
   */
  setPromotionMessages(promotions: unknown): void {
    this.siteData.PromoMessage = promotions;
  }

  /**
   * @param prop - window.siteData property name
   * @param propData - window.siteData propety value
   *
   * @description
   * sets property and value based on provided params
   */
  setProperty(prop: string, propData?: unknown): void {
    if (typeof prop === 'string') {
      this.siteData[prop] = propData;
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setReferringPage
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param referringPage - The referring page
   *
   * @description
   * Sets the Referring Page on the Object. The Page which referred the user to the page that they are currently on.
   */
  setReferringPage(referringPage: string): void {
    this.siteData.ReferringPage = referringPage;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setReferringPageType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param referringPageType - The referring page Type
   *
   * @description
   * Sets the Referring Page Type on the Object. The type of page which referred the user to the page that they are currently on.
   */
  setReferringPageType(referringPageType: string): void {
    this.siteData.ReferringPageType = referringPageType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setReferringPageUrl
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param referringPageUrl - The referring page URL
   *
   * @description
   * Sets the Referring Page URL on the Object. The URL of the page which referred the user to the page that they are currently on.
   */
  setReferringPageUrl(referringPageUrl: string | undefined): void {
    this.siteData.ReferringPageURL = referringPageUrl ? decodeURI(referringPageUrl) : '';
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setRegistrationOccurred
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param registrationOccurred - The registration occurred flag
   *
   * @description
   * Sets the Referring Page URL on the Object. The URL of the page which referred the user to the page that they are currently on.
   */
  setRegistrationOccurred(registrationOccurred: string): void {
    this.siteData.RegistrationOccured = registrationOccurred;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setReplacementTerm
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param replacementTerm - The replacement term
   *
   * @description
   * Sets the replacement term for search on the Object.
   */
  setReplacementTerm(replacementTerm: string): void {
    this.siteData.ReplacementTerm = replacementTerm;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setReplacementType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param replacementType - The replacement type
   *
   * @description
   * Sets the replacement type for search on the Object.
   */
  setReplacementType(replacementType: string): void {
    this.siteData.ReplacementType = replacementType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSalesCatalogDetails
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param salesCatalogDetails
   *
   * @description
   * Sets the Sales Category Details on the Object.
   */
  setSalesCatalogDetails(salesCatalogDetails: object[]): void {
    this.siteData.MCSalesCatalogDetails = salesCatalogDetails;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSearchResultCount
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param searchResultCount - The search results count
   *
   * @description
   * Sets the search results count on the Object.
   */
  setSearchResultCount(searchResultCount: string): void {
    this.siteData.SearchResultCount = searchResultCount;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSearchType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param searchType - The search type
   *
   * @description
   * Sets the search type on the Object.
   */
  setSearchType(searchType: string): void {
    this.siteData.SearchType = searchType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSearchType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param actionType - The action type
   *
   * @description
   * Sets the action type on the siteData object.
   */
  setActionType(actionType: string): void {
    this.siteData.ActionType = actionType;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setServerDate
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param date - The current Date String, formatted MM-DD-YYYY
   *
   * @description
   * Sets the Server Date on the Object. Date that the user accessed the page.
   */
  setServerDate(date: string): void {
    this.siteData.ServerDate = date;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setServerTime
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param time - The current Time String, formatted HH:mm
   *
   * @description
   * Sets the Server Time on the Object. Time that the user accessed the page.
   */
  setServerTime(time: string): void {
    this.siteData.ServerTime = time;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSessionID
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param sessionId - The current Session ID, as a v4 UUID
   *
   * @description
   * Sets the Session ID on the Object. Unique identifier for the specific full visit for the user.
   */
  setSessionID(sessionId: string): void {
    this.siteData.SessionID = sessionId;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoggedIn
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param shouldBeLoggedIn - The Should Be Logged In flag as a Boolean
   *
   * @description
   * Sets the Should Be Signed In status on the Object. This denotes whether the session should have expired or not.
   */
  setShouldBeLoggedIn(shouldBeLoggedIn: boolean): void {
    this.siteData.ShouldBeLoggedIn = `${shouldBeLoggedIn}`;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSortMethod
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param sortMethod - The sort method
   *
   * @description
   * Sets the sort method on the Object.
   */
  setSortMethod(sortMethod: string): void {
    this.siteData.SortMethod = sortMethod;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setStaticID
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param staticId - The title of the current state
   *
   * @description
   * Sets the page title of the current state.  Duplicate of siteData.Page
   */
  setStaticID(staticId: string): void {
    this.siteData.StaticID = staticId;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setType
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param type - The update type, either PageType or EventType
   * @param value - The value to set for the type
   *
   * @description
   * Sets the type of update, will either be a page update or an event update and will call the appropriate method accordingly
   * to set the correct siteData property.  If an existing type already exists, it will remove the incorrect type
   */
  setType(type: string, value: string): void {
    // Check the Type
    if (typeof type === 'string' && type.toLowerCase() === 'event') {
      // Check if PageType is present
      if (this.siteData.PageType) {
        // Delete the EventType property
        delete this.siteData.PageType;
      }

      // Set the EventType
      this.setEventType(value);
    } else {
      // Check if EvenType is present
      if (this.siteData.EventType) {
        // Delete the EventType property
        delete this.siteData.EventType;
      }

      // Set the PageType
      this.setPageType(value);
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setUserID
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param userID - The ID of the current user, either guest or registered
   *
   * @description
   * Sets the user ID on the Object. The unique identifier for the user currently browsing the site as either
   * guest or registered.
   */
  setUserID(userID: string): void {
    this.siteData.UserID = userID;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setVisitNumber
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param visitNumber - The visit number
   *
   * @description
   * Sets the visit number on the Object. This denotes how many times the user has been on the site. Resets
   * when the user clears cache or cookies.
   */
  setVisitNumber(visitNumber: number): void {
    this.siteData.VisitNumber = `${visitNumber}`;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setSearchPageBaseProperties
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param search - The search details
   *
   * @description
   * Sets the search details properties.  These properties are base search properties and are required on siteData
   * regardless if a search was executed or not.
   */
  setSearchPageBaseProperties(search?: {
    recordSetTotal?: string;
    searchDetails: {
      DYMTerm: string;
      DYMType: string;
      replacementTerm: string;
      searchTerm: string;
      searchType: string;
    };
  }): void {
    // Default replacement type
    let replacementType;

    // Initialize an empty search Object if search is undefined, i.e. no search was performed
    search ??= { ...DCSG_CONSTANTS.analyticsReporting.searchEmptyResults };

    // Set DYMTerm
    this.setDYMTerm(search.searchDetails.DYMTerm || '');

    // Set DYMType
    this.setDYMType(search.searchDetails.DYMType || '');

    // Set the Entered Search Term
    this.setEnteredSearchTerm(search.searchDetails.searchTerm || '');

    // Set the Results Count
    this.setSearchResultCount(
      search.recordSetTotal !== undefined &&
        search.recordSetTotal !== null &&
        Number(search.recordSetTotal) >= 0
        ? `${search.recordSetTotal}`
        : '',
    );

    // Set the searchType
    this.setSearchType(search.searchDetails.searchType || '');

    // Check to see if a Did You Mean Term is set
    if (
      typeof search.searchDetails.DYMTerm === 'string' &&
      search.searchDetails.DYMTerm.trim() !== ''
    ) {
      // Check to see if the search term is in the Did You Mean term
      if (search.searchDetails.DYMTerm.startsWith(search.searchDetails.searchTerm)) {
        replacementType = 'Addition';
      } else {
        replacementType = 'Replacement';
      }
    }

    // Set Replacement Term
    this.setReplacementTerm(search.searchDetails.replacementTerm || '');

    // Set Replacement Type
    this.setReplacementType(replacementType || '');
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setIdentityId
   * @methodOf dcsg.app.services.service:setIdentityId
   *
   * @param identityId
   *
   * @description
   * Sets IdentityId
   */
  setIdentityId(identityId: string): void {
    this.siteData.IdentityId = identityId;
  }

  // Overrides are necessary here to tell TypeScript "if eventData is not passed in, we return siteData".
  // This is standard TypeScript syntax and is the cleanest way to do this.
  createCustomEvent(eventName: string): CustomEvent<Window['siteData']>;
  createCustomEvent(eventName: string, eventData: undefined): CustomEvent<Window['siteData']>;
  createCustomEvent<T>(eventName: string, eventData: T): CustomEvent<T>;

  /**
   * @description
   * Utility function to create and return a custom event
   *
   * @param eventName - The name of the Event to create
   * @param eventData - The data to pass to the created Event
   * @returns The created custom Event
   */
  createCustomEvent<T>(eventName: string, eventData?: T): CustomEvent<T | Window['siteData']> {
    const detail = eventData ?? this.siteData;
    return new CustomEvent<T | Window['siteData']>(eventName, { detail });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerPreRenderEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:PagePreRender
   *
   * @param eventName - The name of the render event, i.e. reporting:PagePreRender, reporting:PagePostRender, reporting:update
   * @param eventData - The data to pass along with the event
   * @param filterFn - Optional filter function to modify additionalDetails
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerAnalyticsEvent<T extends object>(
    eventName: string,
    eventData?: T,
    filterFn?: (details: unknown) => unknown,
  ): void {
    const dispatch = () => {
      const data = { ...(eventData ?? this.siteData) };
      const additionalDetails = this.getAdditionalDetailsWithDsgUID(data);
      const logDetails = filterFn ? filterFn(additionalDetails) : additionalDetails;

      this.logger.info(
        `AnalyticsService.triggerRenderEvent -- Event ${eventName} triggered: `,
        logDetails,
      );

      document.dispatchEvent(this.createCustomEvent(eventName, additionalDetails));
    };

    this.zone.runOutsideAngular(dispatch);
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerPreRenderEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:PagePreRender
   *
   * @param eventName - The name of the render event, i.e. reporting:PagePreRender, reporting:PagePostRender, reporting:update
   * @param eventData - The data to pass along with the event
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerAnalyticsEventAndData(eventName: string, eventData?: { [key: string]: unknown }): void {
    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(
        this.createCustomEvent(`${eventName}`, { ...eventData, ...this.siteData }),
      );
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerAnalyticsEventAsPromise
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param eventName - The name of the render event, i.e. NewPageView, CheckoutAction, OrderSubmit, etc.
   * @param eventData - The data to pass along with the event
   * @param timeout - The time to wait before
   *
   * @description
   * Calls analytics function that returns a Promise and passes the Object as data. If a timeout is not provided,
   * then the function within _dsgTag will reject immediately.  This function will wait until Adobe has returned
   * success that the pixel has been received, so it can be used for async logic control.  Example: send PaymentType
   * property to analytics on Payments page before redirecting to Order Confirmation page so that we can ensure the
   * data has been successfully sent to Adobe before triggering a URL change and causing any open XHRs (e.g. Adobe pixel)
   * to be cancelled.  If for some reason the function is unavailable from analytics, this function will simply return a
   * rejected Promise.  Additionally, if the Event name does not exist in analytics or isn't being handled there, it will
   * simply auto-reject the Promise.
   */
  triggerAnalyticsEventAsPromise(
    eventName: string,
    eventData: Record<string, string | number | boolean | null>,
    timeout: number,
  ): Promise<void> {
    return this.zone.runOutsideAngular(() => {
      // Ensure the analytics function exists
      if (
        typeof this.window._dsgTag?.FunctionRepository?.TriggerAnalyticsEventAsPromise ===
        'function'
      ) {
        return this.window._dsgTag.FunctionRepository.TriggerAnalyticsEventAsPromise(
          eventName,
          eventData,
          timeout,
        );
      }

      // Default to rejecting the Promise
      return Promise.reject(
        new Error('TriggerAnalyticsEventAsPromise function does not exist yet.'),
      );
    });
  }

  private isKeyOf<T extends object>(object: T, key: PropertyKey): key is keyof T {
    return key in object;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerCategoryAffinityEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param attributeIdentifier - The descriptive attribute's identifier
   * @param attributeName - The descriptive attribute's name
   * @param attributeValue = The descriptive attribute's value
   *
   * @description
   * Triggers a category affinity event to Adobe Target. Values sent through this method
   * are used to drive Target's decisioning for personalization experiences. The value Target
   * receives is constructed using a map between a the combination of a descriptive
   * attribute's identifier and value and some pre-determined values provided by PZ.
   *
   */
  triggerCategoryAffinityEvent(
    attributeIdentifier: string,
    attributeName: string,
    attributeValue: number | string,
  ): void {
    this.zone.runOutsideAngular(() => {
      const { attributeIDToCategoryMap, supportedAttributes, valueMap } =
        DCSG_CONSTANTS.categoryAffinity;

      // Check to see if the descriptive attribute name is supported
      if (!supportedAttributes.includes(attributeName)) {
        this.logger.info(
          'Unsupported descriptive attribute name provided for Category Affinity: ',
          attributeName,
        );
        return;
      }

      // Get the PZ category value that is mapped to the attribute identifier
      if (!this.isKeyOf(attributeIDToCategoryMap, attributeIdentifier)) {
        this.logger.info(
          'Unmapped attribute identifier provided for Category Affinity: ',
          attributeIdentifier,
        );
        return;
      }
      const category = attributeIDToCategoryMap[attributeIdentifier];

      if (!category || !this.isKeyOf(valueMap, category)) {
        this.logger.info(
          'Unmapped attribute identifier provided for Category Affinity: ',
          attributeIdentifier,
        );
        return;
      }

      // Get the category affinity value for target based on the category and descriptive attribute's value
      const categoryValueMap = valueMap[category];
      const normalizedAttributeValue = `${attributeValue}`;
      if (!this.isKeyOf(categoryValueMap, normalizedAttributeValue)) {
        this.logger.info(
          'Unsupported attribute value provided for Category Affinity: ',
          attributeValue,
        );
        return;
      }

      const affinityValue = categoryValueMap[normalizedAttributeValue];
      if (!affinityValue) {
        this.logger.info(
          'Unsupported attribute value provided for Category Affinity: ',
          attributeValue,
        );
        return;
      }

      if (!this.window.alloy) {
        return;
      }

      // Send the category affinity value to Target
      this.window.alloy('sendEvent', {
        data: {
          __adobe: {
            target: {
              'user.categoryId': affinityValue,
            },
          },
        },
        xdm: {
          _experience: {
            decisioning: {
              propositions: [
                {
                  scope: 'cta-scope',
                },
              ],
              propositionEventType: {
                display: 1,
              },
            },
          },
          eventType: 'decisioning.propositionDisplay',
        },
      });

      this.logger.info('Category affinity event fired: ', affinityValue);
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerCheckoutActionEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param eventName - The name of the render event, i.e. reporting:PagePreRender, reporting:PagePostRender, reporting:update
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerCheckoutActionEvent(eventName: string): void {
    this.siteData.EventType = 'CheckoutAction';
    this.siteData.ActionType = eventName;

    this.logger.log(
      'AnalyticsService.triggerChecktoutActionEvent -- Event CheckoutAction triggered: ',
      this.siteData,
    );

    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(this.createCustomEvent(`${eventName}`, { ...this.siteData }));
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerEmailFormSubmit
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:update
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerEmailFormSubmit(SignUpMessage: string, EAthlete?: string): void {
    this.logger.info(
      'AnalyticsService.triggerEmailFormSubmit -- Event reporting: update triggered: ',
      this.getAdditionalDetailsWithDsgUID(),
    );
    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(
        this.createCustomEvent('EmailSignup', {
          SignUpMessage,
          EAthlete,
        }),
      );
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerPostRenderEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:PagePostRender
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerPostRenderEvent(): void {
    this.logger.info(
      'AnalyticsService.triggerPostRenderEvent -- Event reporting:PagePostRender triggered: ',
      { ...this.siteData },
    );

    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(
        this.createCustomEvent('reporting:PagePostRender', { ...this.siteData }),
      );
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#trigger3rdPartyTags
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#trigger3rdPartyTags
   *
   * @description
   * Triggers the Event to notify Utag to start loading non-essential third party tags.
   */
  trigger3rdPartyTags(): void {
    this.logger.info(
      'AnalyticsService.trigger3rdPartyTags -- Event trigger3rdPartyTags triggered: ',
      { ...this.siteData },
    );

    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(this.createCustomEvent('trigger3rdPartyTags', { ...this.siteData }));
    });
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerDataEmCall
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#triggerDataEmCall
   *
   * @description
   * Triggers the event to notify Utag which value to use for data-em attribute (prop66 in AEM analytics).
   * Should be used when we want to fire prop66 analytics on a non-navigating action.
   */
  triggerDataEmCall(tagName: string): void {
    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(this.createCustomEvent('nonMerchLinkTracking', { dataEm: tagName }));
    });
  }

  protected getAdditionalDetailsWithDsgUID(): Record<string, unknown> & { DSG_UID: string };
  protected getAdditionalDetailsWithDsgUID<T extends object>(
    sourceObject: T,
  ): T & { DSG_UID: string };
  protected getAdditionalDetailsWithDsgUID<T extends object>(
    sourceObject: T | Record<string, unknown> = this.siteData,
  ): (T | Record<string, unknown>) & {
    DSG_UID: string;
  } {
    return { ...sourceObject, DSG_UID: this.cookieService.get('DSG_UID') };
  }

  generateLogError(
    errorKey: string,
    errorMessage: string,
    error: object,
    additionalData: Record<string, unknown> | undefined = undefined,
    logLevel: LoggerLevel = LoggerLevel.ERROR,
  ): void {
    // Check to see if there is additional data to merge in to the object
    if (additionalData !== undefined && !!error) {
      // Merge the objects
      error = { ...error, ...additionalData };
    }
    switch (logLevel) {
      case LoggerLevel.INFO:
        this.logger.info(errorKey, error);
        break;
      case LoggerLevel.WARN:
        this.logger.warn(errorKey, error);
        break;
      default:
        this.logger.error(errorKey, error);
        break;
    }

    this.singleErrorToAnalytics(errorMessage);
  }

  /**
   * @description
   * Function that capture the error events for Analytics
   */
  singleErrorToAnalytics(errorString: string): void {
    const errorData: { EventType: string; ErrorMessage: string[]; ErrorId: string[] } = {
      EventType: 'SiteError',
      ErrorMessage: [],
      ErrorId: [],
    };
    errorData.ErrorMessage.push(errorString);
    errorData.ErrorId.push('inline');

    this.setErrorId(errorData.ErrorId);

    this.setErrorMessage(errorData.ErrorMessage);

    this.triggerSiteErrorEvent(errorData);
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerUpdateNotification
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:update
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  triggerSiteErrorEvent(eventData: {
    EventType?: string;
    ErrorMessage: string | string[];
    ErrorId?: string[];
  }): void {
    delete this.siteData.ActionType;
    this.siteData.EventType = 'SiteError';

    const additionalDetails = this.getAdditionalDetailsWithDsgUID(eventData);
    this.logger.info(
      'AnalyticsService.triggerSiteErrorEvent -- Event SiteError triggered: ',
      additionalDetails,
    );

    this.zone.runOutsideAngular(() => {
      document.dispatchEvent(this.createCustomEvent('SiteError', additionalDetails));
    });
  }
}
